import { FC, SyntheticEvent } from "react";
import { useParams } from "react-router-dom";

import { Box, Button, Chip, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import { LiveTagStyling } from "@/components/Procedures/Checklist/utils/Types";

import { AlertConfirmStepOverride } from "@/helpers/alerts/AlertConfirm";

import { ListenerTag } from "@/model/Procedure/ListenerTag";
import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { UpdateStepStatusRequest } from "@/model/Procedure/UpdateStepStatusRequest";
import { SavedLiveTagRequest } from "@/model/SATOSLive/Settings/SavedTagValue";

import { useMutateSaveLiveTagValue } from "@/service/LiveMutations";
import { useUpdateStepStatus } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

interface ListenerTagRowProps {
	isLast: boolean;
	listenerTag: ListenerTag;
	step: ProcedureNodeStep;
	checklistId: number | undefined;
	styling: LiveTagStyling;
}

export const ListenerTagRow: FC<ListenerTagRowProps> = ({
	listenerTag,
	step,
	isLast,
	checklistId,
	styling,
}) => {
	const { id } = useParams<RouteParams>();
	const procedureId = Number(id);
	const { timezone } = useTime();
	const updateStepState = useUpdateStepStatus();
	const saveLiveTagValue = useMutateSaveLiveTagValue();

	const formatTimestamp = (timestamp: string | null | undefined) => {
		if (!timestamp) return "";
		return dayjs.utc(timestamp).tz(timezone).format("DD/MM/YY  HH:mm");
	};

	const value = listenerTag.SavedTagValue
		? listenerTag.SavedTagValue
		: listenerTag.CurrentValue;

	const saveStatusAndValue = async () => {
		const request: UpdateStepStatusRequest = {
			ArchiveType: "Status",
			StepId: step.Id,
			ProcedureId: procedureId,
			File: null,
			SchemeId: checklistId,
			IsOverridden: true,
			Status: 1,
			Metadata: { ...step, Status: 0 },
		};

		await updateStepState(request);

		if (listenerTag.CurrentValue) {
			const saveTagRequest: SavedLiveTagRequest = {
				StepId: step.Id,
				ProcedureId: procedureId,
				SchemeId: checklistId,
				SavedTagValue: listenerTag.CurrentValue,
				Tag: listenerTag?.Tag,
				TagId: listenerTag?.Id,
			};

			await saveLiveTagValue(saveTagRequest);
		}
	};

	const handleOverride = (e: SyntheticEvent) => {
		e.preventDefault();

		AlertConfirmStepOverride(saveStatusAndValue);
	};

	return (
		<Box sx={{ display: "flex", gap: "3rem", overflow: "visible" }}>
			<Box
				sx={{
					textAlign: "center",
				}}
			>
				<Chip
					label={
						<>
							<Box
								sx={{
									borderRadius: "50%",
								}}
							></Box>
							{value !== null ? (
								<>
									{listenerTag?.Name}
									<Translate String="Procedure.ListenerTagRow.Live">
										{" "}
										Live:{" "}
									</Translate>
									{value}
								</>
							) : (
								<>
									{listenerTag?.Name}{" "}
									<Translate String="Procedure.ListenerTagRow.UnableToRetrieveLiveData">
										Live: Unable to retrieve live data
									</Translate>
								</>
							)}
						</>
					}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						verticalAlign: "center",
						width: "fit-content",
						transition: ".2s all ease-in-out",
						position: "relative",
						overflow: "hidden",
						...styling?.container,
						"&:before": styling?.pulseAnimation,
					}}
				/>
				{!Number.isNaN(listenerTag?.SavedTagValue) &&
				listenerTag.SavedTagValue ? (
					<Typography variant="caption">
						Value is from: {formatTimestamp(listenerTag?.SavedTagTimestamp)}
					</Typography>
				) : step.IsOverridden ? (
					<Typography variant="caption">
						<Translate String="Checklist.Step.Overridden">
							Step was overridden
						</Translate>
					</Typography>
				) : null}
			</Box>

			{isLast && !step.Status && (
				<Box>
					<Button
						variant="outlined"
						color="warning"
						size="small"
						onClick={handleOverride}
						sx={{
							height: "30px",
						}}
					>
						<Translate String="Checklist.Step.Override">
							Override step
						</Translate>
					</Button>
				</Box>
			)}
		</Box>
	);
};
